import type { GraftPlainString } from './readGraftObject.type'

const GRAFT_GETTER_PREFIX = 'get_'

/** Coerce graft plain string fields (nullable at runtime) into required app strings. */
export function graftPlainString(value: GraftPlainString | null | undefined, fallback = ''): string {
  return value ?? fallback
}

/** Matches Graft JS getters (`get_projectId`) and .NET-style (`get_ProjectId` → `projectId`). */
function graftGetterPropertyKey(getterName: string): string {
  const propertyName = getterName.slice(GRAFT_GETTER_PREFIX.length)
  return propertyName.charAt(0).toLowerCase() + propertyName.slice(1)
}

function isGraftGetter(value: unknown): value is (this: object) => unknown {
  return typeof value === 'function'
}

function collectGraftGetterNames(entity: object): string[] {
  const names = new Set<string>()
  const sources = [entity, Object.getPrototypeOf(entity)]

  for (const source of sources) {
    if (!source) continue

    for (const name of Object.getOwnPropertyNames(source)) {
      if (name.startsWith(GRAFT_GETTER_PREFIX)) {
        names.add(name)
      }
    }
  }

  return [...names]
}

function graftGetters(entity: object): Array<readonly [string, (this: object) => unknown]> {
  return collectGraftGetterNames(entity).flatMap((name) => {
    const read = (entity as Record<string, unknown>)[name]
    return isGraftGetter(read) ? [[name, read] as const] : []
  })
}

async function readGraftValue(value: unknown, seen: WeakSet<object>): Promise<unknown> {
  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => readGraftValue(item, seen)))
  }

  if (value && typeof value === 'object') {
    return readGraftObject(value, seen)
  }

  return value
}

export async function readGraftObject<T extends object>(entity: object, seen = new WeakSet<object>()): Promise<T> {
  if (seen.has(entity)) {
    return {} as T
  }

  const readers = graftGetters(entity)
  if (readers.length === 0) {
    return entity as T
  }

  seen.add(entity)

  const entries = (
    await Promise.all(
      readers.map(async ([name, read]) => {
        try {
          const value = await readGraftValue(await read.call(entity), seen)
          return [graftGetterPropertyKey(name), value] as const
        } catch {
          return null
        }
      })
    )
  ).filter((entry): entry is readonly [string, unknown] => entry !== null)

  return Object.fromEntries(entries) as T
}
